import type {QueryClient} from '@tanstack/react-query';
import type {ConversationDetails} from '@/types/conversationMessage';
import type {CampaignCreatorApiResponse} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import {CAMPAIGN_V2_CREATORS_COUNTS_BASE_QUERY_KEY} from '@/hooks/queries/use-campaign-v2-creators-counts-query';
import {CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY} from '@/hooks/queries/use-campaign-v2-creators-query';
import {CONVERSATION_DETAILS_QUERY_KEY} from '@/hooks/queries/use-conversation-details-query/useConversationDetailsQuery';

type CampaignCreatorsInfiniteData = {
  pages?: {data: CampaignCreatorApiResponse[]}[];
  pageParams?: unknown[];
};

type UnreadPatch = {
  conversationId: string;
  campaignId?: string;
};

function creatorsQueryKey(campaignId?: string) {
  return campaignId
    ? [CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY, {campaignId}]
    : [CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY];
}

function countsQueryKey(campaignId: string) {
  return [CAMPAIGN_V2_CREATORS_COUNTS_BASE_QUERY_KEY, {campaignId}];
}

function patchConversationDetails(
  queryClient: QueryClient,
  conversationId: string,
  update: (details: ConversationDetails) => ConversationDetails
) {
  queryClient.setQueriesData<ConversationDetails>(
    {queryKey: [CONVERSATION_DETAILS_QUERY_KEY, conversationId]},
    (old) => {
      if (!old) {
        return old;
      }
      return update(old);
    }
  );
}

function mapMatchingCreators(
  queryClient: QueryClient,
  {conversationId, campaignId}: UnreadPatch,
  update: (creator: CampaignCreatorApiResponse) => CampaignCreatorApiResponse
) {
  queryClient.setQueriesData<CampaignCreatorsInfiniteData>(
    {queryKey: creatorsQueryKey(campaignId), exact: false},
    (old) => {
      if (!old?.pages) {
        return old;
      }
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          data: page.data.map((creator) =>
            creator.merchantConversationId === conversationId
              ? update(creator)
              : creator
          ),
        })),
      };
    }
  );
}

function campaignUnreadFromCreatorsList(
  queryClient: QueryClient,
  {conversationId, campaignId}: UnreadPatch
): number | undefined {
  if (!campaignId) {
    return undefined;
  }

  const match = queryClient
    .getQueriesData<CampaignCreatorsInfiniteData>({
      queryKey: creatorsQueryKey(campaignId),
      exact: false,
    })
    .flatMap(([, data]) => data?.pages ?? [])
    .flatMap((page) => page.data)
    .find((row) => row.merchantConversationId === conversationId);

  if (!match) {
    return undefined;
  }
  return match.merchantCampaignUnreadMessagesCount ?? 0;
}

function mapCampaignConversations(
  details: ConversationDetails,
  campaignId: string | undefined,
  updateCount: (count: number) => number
): ConversationDetails['campaignConversations'] {
  return details.campaignConversations.map((conversation) => {
    if (campaignId && conversation.campaign.id !== campaignId) {
      return conversation;
    }
    return {
      ...conversation,
      unreadMessagesCount: updateCount(conversation.unreadMessagesCount),
    };
  });
}

function remainingUnreadAfterClearingCampaign(
  details: ConversationDetails,
  campaignId: string | undefined,
  campaignUnreadFromList: number | undefined
): number {
  if (!campaignId) {
    return 0;
  }

  const campaignThread = details.campaignConversations.find(
    (conversation) => conversation.campaign.id === campaignId
  );
  const campaignUnread =
    campaignThread?.unreadMessagesCount ?? campaignUnreadFromList;
  if (campaignUnread === undefined) {
    return 0;
  }
  return Math.max(0, details.unreadMessagesCount - campaignUnread);
}

/**
 * After mark_all_read succeeds, unread UI still reads stale React Query
 * caches. Patch those caches so indicators clear without a hard reload.
 */
function clearUnreadAfterMarkAllRead(
  queryClient: QueryClient,
  {conversationId, campaignId}: UnreadPatch
) {
  const campaignUnreadFromList = campaignUnreadFromCreatorsList(queryClient, {
    conversationId,
    campaignId,
  });
  patchConversationDetails(queryClient, conversationId, (old) => {
    const remaining = remainingUnreadAfterClearingCampaign(
      old,
      campaignId,
      campaignUnreadFromList
    );
    return {
      ...old,
      unreadMessagesCount: remaining,
      firstUnreadMessage: remaining > 0 ? old.firstUnreadMessage : null,
      campaignConversations: mapCampaignConversations(old, campaignId, () => 0),
    };
  });
  mapMatchingCreators(queryClient, {conversationId, campaignId}, (creator) => {
    const campaignUnread = creator.merchantCampaignUnreadMessagesCount ?? 0;
    const totalUnread = creator.merchantUnreadMessagesCount ?? 0;
    return {
      ...creator,
      merchantCampaignUnreadMessagesCount: 0,
      merchantUnreadMessagesCount: campaignId
        ? Math.max(0, totalUnread - campaignUnread)
        : 0,
    };
  });
}

/**
 * Inverse of clearUnreadAfterMarkAllRead. The exact server count is confirmed
 * by invalidateUnreadUiQueries (creators list + conversation details).
 */
export function restoreUnreadAfterMarkUnread(
  queryClient: QueryClient,
  {conversationId, campaignId, messageId}: UnreadPatch & {messageId: string}
) {
  patchConversationDetails(queryClient, conversationId, (old) => ({
    ...old,
    unreadMessagesCount: old.unreadMessagesCount + 1,
    firstUnreadMessage: {id: messageId},
    campaignConversations: campaignId
      ? mapCampaignConversations(old, campaignId, (count) => count + 1)
      : old.campaignConversations,
  }));
  // Without campaignId we cannot tell which campaign list owns the unread.
  // Patching the base key with exact: false would increment every cached
  // campaignV2Creators row for this conversation.
  if (!campaignId) {
    return;
  }
  mapMatchingCreators(queryClient, {conversationId, campaignId}, (creator) => ({
    ...creator,
    merchantCampaignUnreadMessagesCount:
      (creator.merchantCampaignUnreadMessagesCount ?? 0) + 1,
    merchantUnreadMessagesCount: (creator.merchantUnreadMessagesCount ?? 0) + 1,
  }));
}

export function invalidateUnreadUiQueries(
  queryClient: QueryClient,
  {conversationId, campaignId}: UnreadPatch
) {
  queryClient.invalidateQueries({
    queryKey: creatorsQueryKey(campaignId),
  });
  queryClient.invalidateQueries({
    queryKey: [CONVERSATION_DETAILS_QUERY_KEY, conversationId],
  });
  if (campaignId) {
    queryClient.invalidateQueries({
      queryKey: countsQueryKey(campaignId),
    });
  }
}

export default clearUnreadAfterMarkAllRead;
